import type { Article, ActId, Footnote } from '@/types';
import { buildEliUrl } from '@/utils/eliUrl';
import instrumentsData from '@/data/instruments/index.json';
import type { Instrument } from '@/types';
import styles from './ArticleBody.module.css';
const INSTRUMENTS = instrumentsData as Record<string, Instrument>;
interface Props {
  article: Article;
  actId: ActId;
  footnotes?: Footnote[];
  onInstrumentClick?: (instrumentId: string) => void;
  /** Hide the built-in EUR-Lex EN/IT source row (used in the mandate reader, whose
   *  articles are an un-adopted draft RTS — those ELI links would point to the wrong
   *  main-act article). Default false. */
  hideSources?: boolean;
  /** Override the "Read full text" target. Default: the main-act reader route. */
  readerHref?: string;
}
export function ArticleBody({ article, actId, footnotes, onInstrumentClick, hideSources = false, readerHref }: Props) {
  const { textEN, summary, rtsIts } = article;
  const eliUrlEn = buildEliUrl(actId, article.num, 'eng');
  const eliUrlIt = buildEliUrl(actId, article.num, 'ita');
  return (
    <div className={styles.body}>
      {/* Official sources */}
      {!hideSources && (
      <div className={styles.sources} data-tour="art-sources">
        <span className={styles.sourcesLabel}>Official sources — EUR-Lex</span>
        <div className={styles.sourceLinks}>
          <a href={eliUrlEn} target="_blank" rel="noopener noreferrer" className={styles.sourceBtn}>EN ↗</a>
          <a href={eliUrlIt} target="_blank" rel="noopener noreferrer" className={styles.sourceBtn}>IT ↗</a>
        </div>
      </div>
      )}
      {/* Read full text -> standalone reader page in a new tab (hash route) */}
      <a
        className={styles.readerBtn}
        data-tour="read-article"
        href={readerHref ?? `#/article/${actId}/${article.num}`}
        target="_blank"
        rel="noopener"
      >
        &#128218; Read full text &#8599;
      </a>
      {/* Summary */}
      {summary && summary.trim() && (
        <div className={styles.summarySection} data-tour="art-summary">
          <span className={styles.summaryLabel}>Summary</span>
          <p className={styles.summaryText}>{summary}</p>
          {article.summary_disclaimer && (
            <span className={styles.summaryDisclaimer}>{article.summary_disclaimer}</span>
          )}
        </div>
      )}
      {/* RTS/ITS mandates */}
      {rtsIts && rtsIts.length > 0 && (
        <div className={styles.mandates} data-tour="art-mandates">
          <span className={styles.mandatesLabel}>🔗 Linked RTS / ITS / GL mandates</span>
          <div className={styles.mandateList}>
            {rtsIts.map((id) => (
              <MandateBadge key={id} instrumentId={id} onClick={onInstrumentClick} />
            ))}
          </div>
        </div>
      )}
      {/* Footnotes */}
      {textEN?.footnoteRefs && textEN.footnoteRefs.length > 0 && footnotes && (
        <div className={styles.footnotesSection}>
          <span className={styles.footnotesLabel}>📎 Footnotes</span>
          <ol className={styles.footnoteList}>
            {textEN.footnoteRefs.map(n => {
              const fn = footnotes.find(f => f.num === n);
              return fn ? (
                <li key={n} className={styles.footnoteItem}>
                  <span className={styles.footnoteNum}>({n})</span>
                  <span className={styles.footnoteText}>{fn.text}</span>
                </li>
              ) : null;
            })}
          </ol>
        </div>
      )}

    </div>
  );
}


function MandateBadge({ instrumentId, onClick }: { instrumentId: string; onClick?: (id: string) => void }) {
  const inst = INSTRUMENTS[instrumentId];
  if (!inst) return (
    <div className={styles.mandateBadge}>
      <span className={styles.mandateId}>{instrumentId}</span>
    </div>
  );
  const statusColors: Record<string, string> = {
    adopted: '#6D28D9', final_report: '#15803D', consultation_closed: '#1D4ED8',
    consultation_open: '#B45309', upcoming: '#0369A1', planned: '#475569',
  };
  const color = statusColors[inst.status] ?? '#475569';
  return (
    <div
      className={`${styles.mandateBadge} ${onClick ? styles.clickable : ''}`}
      onClick={() => onClick?.(instrumentId)}
      role={onClick ? 'button' : undefined}
    >
      <div className={styles.mandateBadgeTop}>
        <span className={styles.mandateType}>{inst.type}</span>
        <span className={styles.mandateStatus} style={{ color }}>{inst.statusLabel}</span>
      </div>
      <div className={styles.mandateTitle}>{inst.title}</div>
      {inst.documentUrl && (
        <a href={inst.documentUrl} target="_blank" rel="noopener noreferrer" className={styles.mandateLink}>
          Open document ↗
        </a>
      )}
    </div>
  );
}
